import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Tooltip, notification, theme, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store/configureStore';
import { getRiskColumns } from 'constants/risksColumns';
import { QueryScope, RiskRow } from 'store/ducks/InternalControls/types';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { internalControlsOp } from 'store/ducks/InternalControls';
import { ArrayParam, useQueryParams, withDefault } from 'use-query-params';
import ApiService from 'utils/ApiCalls';
import CustomTable from 'components/Tables/CustomTable';
import InternalControlLayout from 'components/Layouts/InternalControlLayout';
import useDeleteWithDependencyCheck from 'utils/hooks/useDeleteWithDependencyCheck';
import * as Styled from '../RiskControl.styled';
import * as LayoutStyled from 'components/Layouts/Layout.styled';

const { useToken } = theme;

const Risks: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { drawTable, selectedEntityId, lang } = useSelector(
    (state: RootState) => ({
      drawTable: state.internalControls.drawTable,
      selectedEntityId: state.internalControls.selectedEntityId,
      lang: state.global.lang
    })
  );

  // Sync language with i18n
  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);
  const [risks, setRisks] = useState<RiskRow[]>([]);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const originalRisksRef = useRef<RiskRow[]>([]);
  const [newRiskIds, setNewRiskIds] = useState<Set<number>>(new Set());
  const [isDirty, setIsDirty] = useState(false);
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const dispatch = useDispatch();

  const { handleDelete: handleRiskDelete } = useDeleteWithDependencyCheck({
    tableName: 'risk_maintenance',
    softDeleteFn: (id) => ApiService.softDeleteRiskMaintenance(Number(id)),
    onSuccess: (id) => setRisks((prev) => prev.filter((r) => r.id !== id)),
    entityLabel: 'risk'
  });

  const trackingSetRisks = (
    newRisks: RiskRow[] | ((prev: RiskRow[]) => RiskRow[])
  ) => {
    if (typeof newRisks === 'function') {
      setRisks((prev) => {
        const result = newRisks(prev);
        setIsDirty(true);
        return result;
      });
    } else {
      setRisks(newRisks);
      setIsDirty(true);
    }
  };

  useEffect(() => {
    setSaveDisabled(!isDirty && newRiskIds.size === 0);
    dispatch(internalControlsOp.setHasChanges(isDirty || newRiskIds.size > 0));
  }, [isDirty, newRiskIds, dispatch]);

  const [probability_of_occurrence, setProbability] = useState<
    { label: string; value: number }[]
  >([]);
  const [severity, setSeverity] = useState<{ label: string; value: number }[]>(
    []
  );
  const [speedOfOccurrence, setSpeedOfOcuurance] = useState<
    { label: string; value: number }[]
  >([]);
  const [riskStatus, setRiskStatus] = useState<
    { label: string; value: number }[]
  >([]);
  const [updateCycle, setUpdateCycle] = useState<
    { label: string; value: number }[]
  >([]);
  const [riskTrend, setRiskTrend] = useState<
    { label: string; value: number }[]
  >([]);
  const { token } = useToken();
  const { scheme } = useSelector((state: RootState) => ({
    scheme: state.global.scheme
  }));
  const FiltersParam = withDefault(ArrayParam, []);

  const [query, setQuery] = useQueryParams({
    group: FiltersParam,
    division: FiltersParam,
    region: FiltersParam,
    legal_entity: FiltersParam,
    business_unit: FiltersParam,
    main_organization: FiltersParam,
    bru_assigned: FiltersParam
  });

  const bruAssignedFilter = (query.bru_assigned?.[0] as string) || undefined;

  const getValidIdFromArray = (value: unknown): number | undefined => {
    if (!Array.isArray(value) || value.length === 0) return undefined;

    const num = Number(value[0]);
    return Number.isInteger(num) && num > 0 ? num : undefined;
  };

  const buildParamsFromQuery = () => {
    const paramMap = [
      { key: 'group', paramKey: 'group_id' },
      { key: 'division', paramKey: 'division_id' },
      { key: 'region', paramKey: 'region_id' },
      { key: 'legal_entity', paramKey: 'legal_entity_id' },
      { key: 'business_unit', paramKey: 'business_unit_id' }
    ];

    const params: any = {};
    paramMap.forEach(({ key, paramKey }) => {
      const id = getValidIdFromArray((query as any)[key]);
      if (id) params[paramKey] = id;
    });
    return params;
  };

  const buildSingleRiskParamFromQuery = () => {
    const params = buildParamsFromQuery();
    const mainOrganizationId = getValidIdFromArray(query.main_organization);
    if (mainOrganizationId) params.main_organization_id = mainOrganizationId;
    return params;
  };

  const isScopedView = useMemo(() => {
    const scope = buildSingleRiskParamFromQuery();
    return Object.keys(scope).length > 0;
  }, [query]);

  const hasMainOrganization =
    query.main_organization &&
    Array.isArray(query.main_organization) &&
    query.main_organization.length > 0;

  useEffect(() => {
    const isQueryCompletelyEmpty = (q: any) => {
      return Object.values(q || {}).every(
        (val) => Array.isArray(val) && val.length === 0
      );
    };

    const fetchRisks = async () => {
      try {
        const isEmptyQuery = isQueryCompletelyEmpty(query);

        if (isEmptyQuery && !bruAssignedFilter) {
          setRisks([]);
          originalRisksRef.current = [];
          return;
        }

        let response;
        const params = buildSingleRiskParamFromQuery();
        if (bruAssignedFilter) {
          (params as any).bru_assigned = bruAssignedFilter;
        }

        if (hasMainOrganization) {
          const mainOrganizationIds = (query.main_organization ?? [])
            .filter((v): v is string => v !== null)
            .map(Number)
            .filter(Boolean);

          response = await ApiService.getOrgRisk({
            main_organization_id: mainOrganizationIds,
            bru_assigned: bruAssignedFilter
          });
        } else {
          response = await ApiService.getRisksBPR(params);
        }

        const apiRisks = response.data.data;

        const mappedRisks = apiRisks.map((item: any) => {
          const instance = item.instances?.[0] ?? {};

          return {
            key: item.id.toString(),
            id: item.id,
            title: item.title,
            description: item.description,
            description1: item.description_1,
            description2: item.description_2,
            category: item.category,
            root_cause: item.root_cause,
            event: item.event,
            key_risk_indicators: item.key_risk_indicators,
            created_at: item.created_at,
            updated_at: item.updated_at,
            notUsed: item.not_used ?? false,
            organization_id: item.organization_id || instance?.organization_id,
            ...instance
          };
        });

        setRisks(mappedRisks);
        originalRisksRef.current = JSON.parse(JSON.stringify(mappedRisks));

        const firstRisk = apiRisks?.[0];
        if (firstRisk?.main_organization_id) {
          setOrganizationId(firstRisk.main_organization_id);
        }

        dispatch(internalControlsOp.setDrawTable(false));
      } catch (error) {
        console.error(error);
      }
    };

    fetchRisks();
  }, [
    drawTable,
    dispatch,
    selectedEntityId,
    query.main_organization?.join(','),
    query.group?.join(','),
    query.division?.join(','),
    query.region?.join(','),
    query.legal_entity?.join(','),
    query.business_unit?.join(','),
    bruAssignedFilter
  ]);

  useEffect(() => {
    const fetchAllDropdowns = async () => {
      try {
        const [
          probabilityRes,
          severityRes,
          speedOfOccurrenceRes,
          riskStatusRes,
          updateCycleRes,
          riskTrendRes
        ] = await Promise.all([
          ApiService.getProbabilityOfOccurenceBPR(),
          ApiService.getSeverityImpactStrengthBPR(),
          ApiService.getSpeedOfOcuuranceBPR(),
          ApiService.getRiskStatusBPR(),
          ApiService.getUpdateCyclesBPR(),
          ApiService.getRiskTrendBPR()
        ]);

        setProbability(
          [...probabilityRes.data.data]
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
            .map((o: any) => ({
              label: o.name,
              value: Number(o.id)
            }))
        );

        setSeverity(
          [...severityRes.data.data]
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
            .map((sev: any) => ({
              label: sev.name,
              value: sev.id
            }))
        );

        setSpeedOfOcuurance(
          [...speedOfOccurrenceRes.data.data]
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
            .map((speed: any) => ({
              label: speed.name,
              value: speed.id
            }))
        );

        setRiskStatus(
          [...riskStatusRes.data.data]
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
            .map((status: any) => ({
              label: status.name,
              value: status.id
            }))
        );
        setUpdateCycle(
          [...updateCycleRes.data.data]
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
            .map((cycle: any) => ({
              label: cycle.name,
              value: cycle.id
            }))
        );
        setRiskTrend(
          [...riskTrendRes.data.data]
            .sort((a: any, b: any) => a.name.localeCompare(b.name))
            .map((trend: any) => ({
              label: trend.name,
              value: trend.id
            }))
        );
      } catch (err) {
        console.error('Failed to fetch dropdown data', err);
      }
    };

    fetchAllDropdowns();
  }, []);

  const risksColumns = useMemo(
    () =>
      getRiskColumns(
        risks,
        probability_of_occurrence,
        severity,
        speedOfOccurrence,
        riskStatus,
        updateCycle,
        riskTrend,
        trackingSetRisks,
        t,
        newRiskIds,
        hasMainOrganization,
        handleRiskDelete
      ),
    [
      risks,
      probability_of_occurrence,
      severity,
      speedOfOccurrence,
      riskStatus,
      updateCycle,
      riskTrend,
      newRiskIds,
      t,
      newRiskIds,
      hasMainOrganization
    ]
  );

  const handleAddRiskRow = () => {
    const newId = generateUniqueId();
    const newRow: RiskRow = {
      id: newId,
      key: (risks.length + 1).toString(),
      instance_id: 0,
      title: '',
      description: '',
      description1: '',
      description2: '',
      category: '',
      root_cause: '',
      event: '',
      impact_consequence: '',
      probability_of_occurrence: { id: null, name: null },
      severity: { id: null, name: null },
      speed_of_occurrence: { id: null, name: null },
      gross_risk: null,
      residual_risk: null,
      risk_assessment_score: null,
      risk_owner: '',
      risk_status: { id: null, name: null },
      update_cycle: { id: null, name: null },
      risk_trend: { id: null, name: null },
      assessment_date: null,
      key_risk_indicators: '',
      owner_maintainer: '',
      start_date: null,
      end_date: '9999-01-01',
      created_at: null,
      updated_at: null,
      isNew: true
    };

    trackingSetRisks((prev) => [...prev, newRow]);
    setNewRiskIds((prev) => new Set([...prev, newId]));
  };

  const buildNormalizedScope = (): QueryScope => {
    const singleScope = buildSingleRiskParamFromQuery();
    return {
      group_id: singleScope.group_id ?? null,
      division_id: singleScope.division_id ?? null,
      region_id: singleScope.region_id ?? null,
      legal_entity_id: singleScope.legal_entity_id ?? null,
      business_unit_id: singleScope.business_unit_id ?? null,
      main_organization_id: singleScope.main_organization_id ?? null
    };
  };

  const preparePayload = (
    risksPerRow: RiskRow[],
    scope: QueryScope,
    mainOrgId?: number | null
  ) => {
    return {
      data_list: risksPerRow.map((risk) => {
        const mainOrgIdValue = hasMainOrganization
          ? scope.main_organization_id
          : mainOrgId || organizationId || (risk as any).main_organization_id;

        const basePayload: any = {
          id: risk.id,
          title: risk.title ?? null,
          description: risk.description ?? null,
          description_1: risk.description1 ?? null,
          description_2: risk.description2 ?? null,
          category: risk.category ?? null,
          root_cause: risk.root_cause ?? null,
          event: risk.event ?? null,
          key_risk_indicators: risk.key_risk_indicators || null,
          main_organization_id: mainOrgIdValue
        };

        if (hasMainOrganization) {
          return basePayload;
        }

        if (risk.instance_id !== 0) {
          return {
            ...basePayload,
            instance: {
              group_id: scope.group_id ?? null,
              division_id: scope.division_id ?? null,
              region_id: scope.region_id ?? null,
              legal_entity_id: scope.legal_entity_id ?? null,
              business_unit_id: scope.business_unit_id ?? null,
              main_organization_id: mainOrgIdValue,
              impact_consequence: risk.impact_consequence ?? null,
              probability_of_occurrence_id:
                risk.probability_of_occurrence?.id ?? null,
              severity_id: risk.severity?.id ?? null,
              risk_assessment_score: risk.risk_assessment_score ?? null,
              speed_of_occurrence_id: risk.speed_of_occurrence?.id ?? null,

              risk_owner: risk.risk_owner ?? null,
              risk_status_id: risk.risk_status?.id ?? null,
              assessment_date: risk.assessment_date ?? null,
              instance_id: risk.instance_id ?? null,
              risk_trend_id: risk.risk_trend?.id ?? null,
              update_cycle_id: risk.update_cycle?.id ?? null,
              start_date: risk.start_date ?? null,
              end_date: risk.end_date ?? null,
              owner_maintainer: risk.owner_maintainer ?? null,
              gross_risk: risk.gross_risk || null,
              residual_risk: risk.residual_risk || null
            }
          };
        }

        return basePayload;
      })
    };
  };

  const handleRiskSave = async () => {
    if (!isDirty && newRiskIds.size === 0) {
      notification.info({
        message: 'No Changes',
        description: 'No changes detected to save.',
        placement: 'topRight',
        duration: 3
      });
      return;
    }

    const changedRisks = risks.filter((risk) => {
      if (newRiskIds.has(risk.id)) return true;
      const original = originalRisksRef.current.find((r) => r.id === risk.id);
      if (!original) return false;

      return JSON.stringify(risk) !== JSON.stringify(original);
    });

    if (changedRisks.length === 0) {
      notification.info({
        message: 'No Changes',
        description: 'No changes detected to save.',
        placement: 'topRight',
        duration: 3
      });
      return;
    }

    const scope = buildNormalizedScope();

    let finalOrganizationId = organizationId;
    if (!finalOrganizationId && !hasMainOrganization) {
      try {
        const params = buildParamsFromQuery();

        if (Object.keys(params).length > 0) {
          const response = await ApiService.getRisksBPR(params);
          const firstRisk = response.data.data?.[0];
          if (firstRisk?.main_organization_id) {
            finalOrganizationId = firstRisk.main_organization_id;
          }
        }
      } catch (error) {
        console.error('Failed to fetch main_organization_id:', error);
      }
    }

    // Update organizationId state for future use
    if (finalOrganizationId && !organizationId) {
      setOrganizationId(finalOrganizationId);
    }

    const payload = preparePayload(changedRisks, scope, finalOrganizationId);

    try {
      const response = await ApiService.saveRisksBPR(payload);
      const clearedRisks = risks.map(({ _pinned, ...rest }) => rest as RiskRow);
      setRisks(clearedRisks);
      originalRisksRef.current = JSON.parse(JSON.stringify(clearedRisks));
      setNewRiskIds(new Set());
      setIsDirty(false);
      dispatch(internalControlsOp.setHasChanges(false));
      notification.success({
        message: 'Risks Saved',
        description:
          response?.data?.message ||
          `${changedRisks.length} risk(s) saved successfully.`,
        placement: 'topRight',
        duration: 3
      });
    } catch (error) {
      const err = error as {
        response?: { data?: { message?: string }; status?: number };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Unknown error occurred';
      const statusCode = err?.response?.status || 'Unknown';

      notification.error({
        message: `Save Failed (${statusCode})`,
        description: errorMessage,
        placement: 'topRight',
        duration: 10
      });
    }
  };

  return (
    <InternalControlLayout onSave={handleRiskSave}>
      <Styled.RiskContentWrapper>
        <Styled.GlobalTooltipStyles />
        <CustomTable
          drawTable={true}
          rowKey='key'
          columns={risksColumns}
          dataSource={risks}
          itemsCount={risks.length}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: risks.length,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) =>
              `${range[0]}-${range[1]} of ${total} items`,
            onChange: (page: number, size: number) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
              }
            }
          }}
          rowClassName={(record: { notUsed: boolean }) =>
            record.notUsed ? 'disabled-row has-row-tooltip' : ''
          }
          onRow={(record: { notUsed: boolean }) => {
            if (!record.notUsed) return {};

            return {
              'data-tooltip':
                'Disabled due to a previous Risk/Control type change.'
            };
          }}
          scroll={{ x: 2500 }}
        />
        <Styled.AddRowArea>
          <Tooltip
            title={
              !hasMainOrganization
                ? 'It is possible to add a risk only if Main Organization is selected.'
                : ''
            }
          >
            <Button
              type='primary'
              onClick={handleAddRiskRow}
              disabled={!hasMainOrganization}
            >
              Add new risk
            </Button>
          </Tooltip>
        </Styled.AddRowArea>
      </Styled.RiskContentWrapper>
    </InternalControlLayout>
  );
};

export default Risks;
